import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { WorkspaceBadge } from "@/components/ui/WorkspaceBadge";
import { useBoardShares } from "@/hooks/useBoardShares";
import { api } from "@/lib/api";
import { resolveWorkspaceColor } from "@/lib/colors";
import { BRAND } from "@/lib/tokens";
import { queryKeys } from "@/lib/queryClient";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID, Workspace } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  /** The board's home workspace — never offered as a share target. */
  homeWorkspaceId: UUID;
  boardName: string;
}

export function ShareBoardDialog({
  open,
  onOpenChange,
  boardId,
  homeWorkspaceId,
  boardName,
}: Props) {
  const { shares, share, unshare } = useBoardShares(boardId, open);
  const wsQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
    enabled: open,
  });
  const [pending, setPending] = useState<UUID | null>(null);

  const sharedIds = new Set(shares.map((s) => s.workspace_id));
  // You can only share into workspaces you belong to (the home one excluded).
  const candidates: Workspace[] = (wsQuery.data ?? []).filter((w) => w.id !== homeWorkspaceId);

  async function toggle(ws: Workspace) {
    const currentlyShared = sharedIds.has(ws.id);
    setPending(ws.id);
    try {
      if (currentlyShared) {
        await unshare.mutateAsync(ws.id);
        showToast(t.shareBoardRemoved, "success");
      } else {
        await share.mutateAsync(ws.id);
        showToast(t.shareBoardAdded, "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    } finally {
      setPending(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => pending === null && onOpenChange(o)}
      title={t.shareBoardTitle}
      description={`${t.shareBoardDescription} (${boardName})`}
      width={460}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
        {candidates.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ink-3)", padding: "8px 2px" }}>
            {t.shareBoardNoWorkspaces}
          </div>
        ) : (
          candidates.map((ws) => {
            const isShared = sharedIds.has(ws.id);
            const busy = pending === ws.id;
            return (
              <button
                key={ws.id}
                type="button"
                onClick={() => toggle(ws)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "9px 11px",
                  background: isShared ? "var(--surface-soft)" : "transparent",
                  border: `1px solid ${isShared ? "var(--hairline)" : "var(--hairline-soft)"}`,
                  borderRadius: 8,
                  cursor: busy ? "wait" : "pointer",
                  textAlign: "left",
                  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                  transition: "background .12s, border-color .12s",
                }}
              >
                <WorkspaceBadge name={ws.name} icon={ws.icon} color={resolveWorkspaceColor(ws)} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink-1)" }}>
                  {ws.name}
                </span>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isShared ? BRAND.blue : "transparent",
                    border: isShared ? "none" : "1.5px solid var(--hairline)",
                    color: "#fff",
                  }}
                >
                  {isShared ? <Icon name="check" size={13} color="#fff" stroke={2.6} /> : null}
                </span>
              </button>
            );
          })
        )}
      </div>
      <div
        style={{
          marginTop: 12,
          paddingTop: 10,
          borderTop: "1px solid var(--hairline-soft)",
          fontSize: 11.5,
          color: "var(--ink-3)",
          lineHeight: 1.4,
        }}
      >
        {t.shareBoardHint}
      </div>
    </Dialog>
  );
}
